/**
 * ContractSummaryStep Component
 * Step 5: Contract summary view with sub-tabs (read-only, available after submission)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Stop as TerminateIcon,
  Send as SubmitIcon,
} from '@mui/icons-material';
import { useContractFormContext } from '../ContractFormContext';
import FeeScheduleTable from '../../FeeScheduleTable';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { useNotification } from '../../../../contexts/NotificationContext';
import { MODULES, PERMISSION_LEVELS } from '../../../../contexts/PermissionConstants';
import { apiPost } from '../../../../utils/apiClient';
import logger from '../../../../utils/logger';
import TabPanel, { a11yProps } from '../../../common/TabPanel';
import SummaryContractDetails from './summary/SummaryContractDetails';
import SummaryAmortizationTab from './summary/SummaryAmortizationTab';
import SummaryPaymentHistory from './summary/SummaryPaymentHistory';
import SummaryApprovalHistory from './summary/SummaryApprovalHistory';
import { DocumentUploadSection } from '../../../documents';
import { apiGet } from '../../../../utils/apiClient';
import useServerPDF from '../../../../hooks/useServerPDF';
import { format } from 'date-fns';
import ContractActionDialogs from './summary/ContractActionDialogs';

function ContractSummaryStep({ onEditContract }) {
  const {
    contractId,
    initialData,
    summaryData,
    setSummaryData,
    summaryLoading,
    setSummaryLoading,
    enableBrokerFeeScheduling,
  } = useContractFormContext();

  const [subTabValue, setSubTabValue] = useState(0);
  const { downloadPDF, isGenerating } = useServerPDF();
  const { hasPermission } = usePermissions();
  const notify = useNotification();
  const canWrite = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.WRITE);
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);

  // Action dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load summary data on mount.
  // No guard on summaryData: a contract can be rejected (and its schedule cleared
  // server-side by sp_contract_reject) between visits to this step, so we must
  // re-fetch every time to avoid showing or re-submitting a stale schedule.
  useEffect(() => {
    if (contractId) {
      loadSummaryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSummaryData is defined below
  }, [contractId]);

  const loadSummaryData = async () => {
    if (!contractId) return;

    setSummaryLoading(true);
    try {
      // Re-fetch the contract itself so the summary always shows the LAST SAVED
      // inputs. ContractEdit loads initialData once and never refreshes it after a
      // save, so in edit mode the summary would otherwise render the pre-save row
      // (e.g. Brokered: No after a broker was just added). Falls back to initialData
      // if this fetch fails so the summary never blanks out.
      const [contractData, scheduleData, historyData, paymentsData, receiptsData, versionsData, adhocData, investorsData, rolloverData, feeScheduleData, feeVersionsData] = await Promise.all([
        apiGet(`/contracts/${contractId}`).catch(() => null),
        apiGet(`/amortization/contract/${contractId}`).catch(() => ({ data: { schedule: [] } })),
        apiGet(`/contracts/${contractId}/approval-history`).catch(() => ({ data: [] })),
        apiGet(`/payments/contract/${contractId}`).catch(() => ({ data: [] })),
        apiGet(`/payments/receipts?contract_id=${contractId}`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${contractId}/schedule-versions`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${contractId}/adhoc-capital`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${contractId}/investors`).catch(() => ({ data: [] })),
        // T3-032/T3-034: the many-to-1 rolled-funds source list, so the summary's "Rolled From"
        // row can list ALL sources (comma-separated), not just the single mirror.
        apiGet(`/contracts/${contractId}/rollover-sources`).catch(() => ({ data: [] })),
        // Feature 30: the broker fee ledger + its versions. Both fall back to an empty shape on
        // failure, like every feed above — a fee problem must never blank the whole summary, and
        // a non-brokered contract legitimately has neither.
        apiGet(`/broker-fees/contract/${contractId}/schedule`).catch(() => ({ data: { rows: [], totals: {} } })),
        apiGet(`/broker-fees/contract/${contractId}/versions`).catch(() => ({ data: [] })),
      ]);

      // GET /contracts/:id returns { success, data: <single contract object> }; the
      // shared extractData() only unwraps arrays, so pull the object out explicitly.
      const freshContract = contractData?.data || (contractData && !contractData.success ? contractData : null) || initialData;

      // Helper to extract data from various response formats
      const extractData = (response) => {
        if (!response) return [];
        if (response?.success && response?.data) return response.data;
        if (response?.data && Array.isArray(response.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
      };

      // Extract schedule
      const extractSchedule = (response) => {
        if (!response) return [];
        if (response?.data?.schedule && Array.isArray(response.data.schedule)) {
          return response.data.schedule;
        }
        if (response?.schedule && Array.isArray(response.schedule)) {
          return response.schedule;
        }
        const data = extractData(response);
        return Array.isArray(data) ? data : [];
      };

      setSummaryData({
        contract: freshContract,
        schedule: extractSchedule(scheduleData),
        approvalHistory: extractData(historyData),
        payments: extractData(paymentsData),
        receipts: extractData(receiptsData),
        scheduleVersions: extractData(versionsData),
        adhocCapitalEvents: extractData(adhocData),
        investors: extractData(investorsData),
        rolloverSources: extractData(rolloverData),
        // The fee ledger is { rows, totals }, not a bare array, so extractData() (which only
        // unwraps arrays) would return [] and silently drop it — pull the object out explicitly,
        // the same way freshContract does above.
        feeSchedule: feeScheduleData?.data?.rows || [],
        feeTotals: feeScheduleData?.data?.totals || {},
        feeVersions: extractData(feeVersionsData),
      });
    } catch (err) {
      logger.error('Error loading summary data', { message: err.message });
      setSummaryData({
        contract: initialData,
        schedule: [],
        approvalHistory: [],
        payments: [],
        receipts: [],
        scheduleVersions: [],
        adhocCapitalEvents: [],
        investors: [],
        rolloverSources: [],
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const handleDownloadContractPDF = async () => {
    if (!contractId) return;

    try {
      const filename = `Contract_${initialData?.reference || contractId}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'contract', id: contractId, filename });
    } catch (err) {
      logger.error('Error downloading contract PDF', { message: err.message });
      notify.error('Failed to generate contract PDF: ' + (err.message || 'Please try again.'));
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setSubmitting(true);
      await apiPost(`/contracts/${contractId}/submit-for-approval`);
      // Reload the page to get updated status
      window.location.reload();
    } catch (err) {
      logger.error('Error submitting for approval', { message: err.message, status: err.response?.status });
      notify.error('Failed to submit for approval: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Check if schedule exists for Draft contracts
  const hasSchedule = summaryData?.schedule?.length > 0;

  if (summaryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading contract summary...</Typography>
      </Box>
    );
  }

  if (!initialData) {
    return (
      <Alert severity="warning">
        Contract data not available. Please go back and save the contract first.
      </Alert>
    );
  }

  // Always render from the freshly-refetched contract (loadSummaryData) so the
  // summary reflects the LAST SAVED inputs; fall back to initialData until that
  // fetch resolves (or if it failed).
  const contract = summaryData?.contract || initialData;

  // Feature 30 — the fee tab exists only where a fee does. Both conditions matter: `is_brokered`
  // because a non-brokered contract has no fee at all, and the tenant setting because a tenant
  // that has not opted into fee scheduling never generates one (the route refuses regardless).
  const showFeeTab = !!contract?.is_brokered && enableBrokerFeeScheduling === true;

  // ⚠⚠ NAMED TAB INDICES — DO NOT REINTRODUCE LITERALS HERE.
  // Fee Schedule sits between Amortization and Documents (user, 2026-09-16), and it is
  // CONDITIONAL, so every tab after it shifts by one when it is absent. MUI assigns tab values by
  // RENDER ORDER, skipping falsy children, so a hardcoded index on any panel below the fee tab
  // silently maps to the wrong panel for one of the two states — the exact defect that hit
  // ContractDetail's Schedule History. Deriving them from one flag makes the two states
  // impossible to get out of step, and inserting a future tab is a one-line change here.
  const IDX_DETAILS = 0;
  const IDX_AMORT = 1;
  const IDX_FEE = showFeeTab ? 2 : -1;      // -1 = not rendered; never matches tabValue
  const afterFee = showFeeTab ? 1 : 0;      // everything below shifts by one when Fee is present
  const IDX_DOCS = 2 + afterFee;
  const IDX_PAYMENTS = 3 + afterFee;
  const IDX_APPROVALS = 4 + afterFee;

  return (
    <Box>
      {/* Sub-tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={subTabValue}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {/* ⚠ RENDER ORDER IS THE CONTRACT — it must match the IDX_* arithmetic above, because
              MUI numbers tabs by the order they render (skipping falsy children). Fee Schedule
              sits between Amortization and Documents, and is shown only when the contract is
              brokered AND the tenant has opted into fee scheduling (61 of 68 dev contracts get
              no tab rather than an empty one). */}
          <Tab label="Contract Details" {...a11yProps(IDX_DETAILS, 'summary')} />
          <Tab label="Amortization Schedule" {...a11yProps(IDX_AMORT, 'summary')} />
          {showFeeTab && <Tab label="Fee Schedule" {...a11yProps(IDX_FEE, 'summary')} />}
          <Tab label="Documents" {...a11yProps(IDX_DOCS, 'summary')} />
          <Tab label="Payment History" {...a11yProps(IDX_PAYMENTS, 'summary')} />
          <Tab label="Approval History" {...a11yProps(IDX_APPROVALS, 'summary')} />
        </Tabs>
      </Paper>

      {/* Sub-tab content */}
      <TabPanel value={subTabValue} index={IDX_DETAILS} id="summary" padding={0}>
        <SummaryContractDetails
          contract={contract}
          summaryData={summaryData}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={IDX_AMORT} id="summary" padding={0}>
        <SummaryAmortizationTab
          contract={contract}
          schedule={summaryData?.schedule || []}
          onRegenerate={loadSummaryData}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={IDX_DOCS} id="summary" padding={0}>
        <DocumentUploadSection
          contractId={contractId}
          investorId={contract?.investor_id}
          title="Contract Documents"
          collapsible={false}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={IDX_PAYMENTS} id="summary" padding={0}>
        <SummaryPaymentHistory
          contract={contract}
          payments={summaryData?.payments || []}
          receipts={summaryData?.receipts || []}
          schedule={summaryData?.schedule || []}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={IDX_APPROVALS} id="summary" padding={0}>
        <SummaryApprovalHistory
          approvalHistory={summaryData?.approvalHistory || []}
        />
      </TabPanel>

      {showFeeTab && (
        <TabPanel value={subTabValue} index={IDX_FEE} id="summary" padding={0}>
          {/* showAllRows: a Draft under review shows its WHOLE fee schedule with no pager, so an
              approver is never asked to click through pages to see what they are approving. Same
              split the amortization schedule already makes between this step and Contract Detail
              (AmortizationScheduleTable.js:57-59). */}
          <FeeScheduleTable
            contract={contract}
            rows={summaryData?.feeSchedule || []}
            totals={summaryData?.feeTotals || {}}
            versions={summaryData?.feeVersions || []}
            currencyCode={contract?.currency_code}
            showAllRows
          />
        </TabPanel>
      )}

      {/* Action Dialogs */}
      <ContractActionDialogs
        contractId={contractId}
        contract={contract}
        approveDialogOpen={approveDialogOpen}
        setApproveDialogOpen={setApproveDialogOpen}
        rejectDialogOpen={rejectDialogOpen}
        setRejectDialogOpen={setRejectDialogOpen}
        terminateDialogOpen={terminateDialogOpen}
        setTerminateDialogOpen={setTerminateDialogOpen}
        onActionComplete={loadSummaryData}
      />
    </Box>
  );
}

export default ContractSummaryStep;
